/**
 * District education profile: school-by-school tables for a district and
 * year, downloaded as an Excel workbook built from SIS.
 */
import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Paper, Select, Stack, Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  ProfileDistrict, ProfileOwnership, districtProfileApi, errorMessage,
} from '../../../services/indicatorRegistryApi';

const THIS_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 6 }, (_, i) => THIS_YEAR - i);

const TABLES = [
  'Enrolment by class and sex',
  'Staff, pupils per staff member and staff nearing retirement',
  'Repeaters by class and repetition rate',
  'Pupils leaving school (dropouts) by reason',
  'Pupils with a disability by type',
  'Water, hygiene and toilets, with pupils per toilet',
  'Furniture, seats and pupils per seat, allowing for shifts',
  'Classrooms, pupils per classroom, facilities and ICT equipment',
];

/** The API answers errors as JSON, but a download request reads them as a blob. */
async function downloadError(e: unknown): Promise<string> {
  const response = (e as { response?: { data?: unknown; status?: number } })?.response;
  const data = response?.data;
  if (data instanceof Blob) {
    try {
      const body = JSON.parse(await data.text());
      const first = Object.values(body)[0];
      return Array.isArray(first) ? String(first[0]) : String(first ?? body.detail ?? 'Download failed.');
    } catch {
      return `The server could not build the profile (error ${response?.status ?? 'unknown'}). `
        + 'Please tell the EMIS team which district and year you chose.';
    }
  }
  return errorMessage(e);
}

const DistrictProfile: React.FC = () => {
  const [districtId, setDistrictId] = useState('');
  const [year, setYear] = useState(THIS_YEAR);
  const [ownership, setOwnership] = useState<ProfileOwnership>('government');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const districts = useQuery({ queryKey: ['profile-districts'], queryFn: districtProfileApi.districts });

  const download = async (list: ProfileDistrict[]) => {
    setError(null);
    try {
      for (const d of list) {
        setBusy(d.name);
        await districtProfileApi.download(d, year, ownership);
      }
    } catch (e) {
      setError(await downloadError(e));
    } finally {
      setBusy(null);
    }
  };

  const chosen = districts.data?.find(d => d.id === districtId);

  return (
    <Box sx={{ maxWidth: 900 }}>
      <Typography variant="h6" gutterBottom>District education profile</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        School-by-school tables for one district, built from SIS when you download them. Totals and ratios are
        Excel formulas over the schools listed, and use the same definitions as the indicators, so the district
        row matches the indicator values.
      </Typography>

      {districts.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(districts.error)}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>District</InputLabel>
            <Select label="District" value={districtId} onChange={e => setDistrictId(e.target.value)}
                    disabled={districts.isLoading}>
              {(districts.data || []).map(d => (
                <MenuItem key={d.id} value={d.id}>{d.name}{d.region ? ` (${d.region})` : ''}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Year</InputLabel>
            <Select label="Year" value={year} onChange={e => setYear(Number(e.target.value))}>
              {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel>Schools</InputLabel>
            <Select label="Schools" value={ownership} onChange={e => setOwnership(e.target.value as ProfileOwnership)}>
              <MenuItem value="government">Government</MenuItem>
              <MenuItem value="private">Private</MenuItem>
              <MenuItem value="all">All schools</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Stack direction="row" spacing={1.5} sx={{ mt: 2 }} alignItems="center">
          <Button variant="contained" startIcon={<Download />} disabled={!chosen || !!busy}
                  onClick={() => chosen && download([chosen])}>
            Download profile
          </Button>
          <Button variant="outlined" disabled={!districts.data?.length || !!busy}
                  onClick={() => districts.data && download(districts.data)}>
            Download all districts
          </Button>
          {busy && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Building {busy}…</Typography>
            </Stack>
          )}
        </Stack>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Paper>

      <Typography variant="subtitle2" gutterBottom>For pre-primary: enrolment. For primary and secondary:</Typography>
      <Box component="ul" sx={{ mt: 0, pl: 3, color: 'text.secondary', typography: 'body2' }}>
        {TABLES.map(t => <li key={t}>{t}</li>)}
      </Box>
      <Alert severity="info" variant="outlined">
        Not in SIS, so not in the profile: Form 2 and Form 4 examination results (ZEC), the JMP water and sanitation
        classification, and teachers by level. The cover sheet of each file explains every table.
      </Alert>
    </Box>
  );
};

export default DistrictProfile;
